import * as React from 'react';
import { AvailabilitySearchQuery, LockOptions } from 'types';
import { AvailabilitySearchResult } from 'components/www/availability-search/AvailabilitySearchResult';
import { CurrencyContext, LocaleContext, consumer } from '@tock/coretypes';
import { Dialog, DialogContent, DialogHeading } from 'omakase';
import { ExperienceCardAction } from 'utils/searchBar';
import { IntlProvider } from 'react-intl';
import { LocalDateTime } from 'js-joda';
import { SearchHandlerContext } from 'components/search/SearchContext';
import { SearchMode, SearchQueryStringParams } from 'components/search/utils';
import { StoreType } from '@tock/shared/utils/connect';
import { useQueryStringParams } from 'hooks/useQueryStringParams';

interface Props {
  open: boolean;
  onClose(): void;
  businessResult?: consumer.SearchOfferingBusiness;
  query: AvailabilitySearchQuery;
  experienceCardAction: ExperienceCardAction;
  stateValue: SearchMode;
  lock: StoreType['lock'];
}

const DIALOG_TITLE = 'search-result';

export const AvailabilitySearchResultsMapDialog: React.FunctionComponent<Props> = ({
  businessResult,
  experienceCardAction,
  lock,
  onClose,
  open,
  query,
  stateValue,
}) => {
  const { lockTimeAndSize, navigateToExperience, navigateToExperienceMenu } = {
    ...React.useContext(SearchHandlerContext),
  };
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  if (!businessResult) {
    return null;
  }
  const business = businessResult.business as consumer.OfferingBusiness;

  const handleOnLockTimeAndSize = (
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.OfferingBusiness
  ) => {
    onClose();
    lockTimeAndSize &&
      lockTimeAndSize(ticketTypeId, selectedSize, dateTime, priceCents, options, business);
  };
  const handleOnNavigateToExperience = (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    experienceListIdx: number
  ) => {
    onClose();
    navigateToExperience && navigateToExperience(business, experience, undefined);
  };
  const handleOnNavigateToExperienceMenu = (
    business: consumer.OfferingBusiness,
    experience: consumer.Offering
  ) => {
    onClose();
    navigateToExperienceMenu && navigateToExperienceMenu(business, experience);
  };

  return (
    <Dialog variant="partial" aria-labelledby={DIALOG_TITLE} open={open} onClose={onClose}>
      <DialogHeading id={DIALOG_TITLE} onClose={onClose}>
        Results for {businessResult?.business?.name}
      </DialogHeading>
      <DialogContent disablePadding>
        <IntlProvider key={business.id} locale={business.locale}>
          <LocaleContext.Provider value={business.locale}>
            <CurrencyContext.Provider value={business.currencyCode}>
              <AvailabilitySearchResult
                business={business}
                experienceCardAction={experienceCardAction}
                hideExperiences={stateValue.stateType === 'togo' ? !queryStringParams.date : false}
                locale={business.locale}
                lock={lock}
                onLockTimeAndSize={handleOnLockTimeAndSize}
                onNavigateToExperience={handleOnNavigateToExperience}
                onNavigateToExperienceMenu={handleOnNavigateToExperienceMenu}
                query={query}
                searchOfferings={businessResult.offering as consumer.SearchOffering[]}
                timeZone={business.timeZone}
              />
            </CurrencyContext.Provider>
          </LocaleContext.Provider>
        </IntlProvider>
      </DialogContent>
    </Dialog>
  );
};
